import { z } from 'zod';
import { dayKeySchema } from './contacts.js';
/**
 * Controlling „Vertrieb" (`GET /controlling/sales`, docs/dev/api-controlling.md § Vertrieb; Nico feedback FB-002,
 * D-323…D-326): per sales partner the appointments of the period with their outcomes, the deals that followed and
 * the current stage of those appointments' deals. The query is shared by the API (validation) and the SPA; the
 * response schema is the API's serializer and the SPA's type.
 */
export const salesPeriods = ['week', 'month', 'quarter', 'year'];
export const salesScopes = ['me', 'team', 'organization'];
export const SALES_RANGE_MESSAGE = 'Bitte einen Zeitraum mit Beginn vor dem Ende angeben.';
export const SALES_RANGE_INCOMPLETE_MESSAGE = 'Bitte Beginn und Ende des Zeitraums angeben.';
export const salesQuerySchema = z
    .object({
    period: z.enum(salesPeriods).default('month'),
    /** A free range of Berlin calendar days (both inclusive) instead of the current `period`; both or neither. */
    from: dayKeySchema.optional(),
    until: dayKeySchema.optional(),
    scope: z.enum(salesScopes).optional(),
    /** One partner of the scope: the response then carries this partner's appointments. */
    userId: z.string().uuid().optional(),
})
    .superRefine((query, ctx) => {
    if ((query.from === undefined) !== (query.until === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: [query.from === undefined ? 'from' : 'until'],
            message: SALES_RANGE_INCOMPLETE_MESSAGE,
        });
        return;
    }
    if (query.from !== undefined && query.until !== undefined && query.from > query.until) {
        ctx.addIssue({ code: 'custom', path: ['until'], message: SALES_RANGE_MESSAGE });
    }
});
export const salesNextStepSchema = z.object({
    /** Stage name of the appointment's deal, „Verloren"/„Storniert" for closed deals, „Kein Deal" without a deal. */
    stage: z.string(),
    count: z.number().int(),
});
export const salesRowSchema = z.object({
    /** null on `totals`. */
    userId: z.string().nullable(),
    /** „J. Weiler"; „Gesamt" on `totals`. */
    name: z.string(),
    /** Role name („Berater"); null on `totals`. */
    role: z.string().nullable(),
    booked: z.number().int(),
    attended: z.number().int(),
    noShow: z.number().int(),
    cancelled: z.number().int(),
    /** attended ÷ (attended + no-show), rounded; null without either. */
    showRatePct: z.number().nullable(),
    dealsNew: z.number().int(),
    reserved: z.number().int(),
    notarized: z.number().int(),
    volumeCents: z.number().int(),
    nextSteps: z.array(salesNextStepSchema),
});
export const salesSetterRowSchema = z.object({
    userId: z.string(),
    name: z.string(),
    role: z.string().nullable(),
    booked: z.number().int(),
    attended: z.number().int(),
    noShow: z.number().int(),
    showRatePct: z.number().nullable(),
});
export const salesAppointmentStatuses = [
    'booked',
    'rescheduled',
    'cancelled',
    'attended',
    'no_show',
];
export const salesAppointmentRowSchema = z.object({
    id: z.string(),
    /** Start (ISO instant). */
    at: z.string(),
    /** null when the viewer may not see the contact (or the appointment has none). */
    contactName: z.string().nullable(),
    contactId: z.string().nullable(),
    status: z.enum(salesAppointmentStatuses),
    outcome: z.enum(['attended', 'no_show', 'rescheduled']).nullable(),
    /** Current stage of the deal (closed deals: „Verloren"/„Storniert"); null without a deal. */
    dealStage: z.string().nullable(),
    dealId: z.string().nullable(),
});
export const salesResponseSchema = z.object({
    /** title „Vertrieb", line „Team Regensburg · September 2026". */
    header: z.object({ title: z.string(), line: z.string() }),
    /** `custom` when `from`/`until` were given. */
    period: z.enum([...salesPeriods, 'custom']),
    /** The Berlin days of the period, `until` inclusive. */
    range: z.object({ from: z.string(), until: z.string() }),
    scope: z.enum(salesScopes),
    scopes: z.array(z.object({ key: z.enum(salesScopes), label: z.string() })),
    totals: salesRowSchema,
    rows: z.array(salesRowSchema),
    setters: z.array(salesSetterRowSchema),
    /** The partner whose appointments are listed (scope `me`: the viewer; else the `userId` filter), or null. */
    appointmentsOf: z.object({ userId: z.string(), name: z.string() }).nullable(),
    appointments: z.array(salesAppointmentRowSchema),
});
export const SALES_TITLE = 'Vertrieb';
export const SALES_TOTAL_LABEL = 'Gesamt';
export const SALES_NO_DEAL_LABEL = 'Kein Deal';
export const SALES_EMPTY_TEXT = 'Noch keine Termine im Zeitraum.';
//# sourceMappingURL=controlling-sales.js.map